import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTrash, faPlus, faLightbulb, faPencilAlt, faCheck, faTasks, faSpinner, faChevronDown, faChevronUp, faCopy, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

/**
 * Enhanced PlotItems Component with improved suggestion and evaluation handling
 */
const PlotItems = ({
  plotItems,
  level,
  onUpdate,
  onReorder,
  onDelete,
  suggestions,
  setSuggestions,
  workspaceUuid,
  workspaceData,
  onSelectPlotItem,
  onReplaceAllPlotItems,
  evaluations,
  onSendQuestionToChat,
  todoItems,
  onAddTodoItem,
  onGetSuggestions,
  onShowPreviousEvaluations, // New prop
  sceneEvaluations = [], // New prop
  isEvaluatingWorkspace = false,
  onEvaluateWorkspace,
  onUpdateAiCalls // New prop for updating AI calls count
}) => {
  // Index of item currently being dragged (for reordering)
  const [draggedIndex, setDraggedIndex] = useState(null);
  // Track which item is selected (highlighted)
  const [selectedPlotItemId, setSelectedPlotItemId] = useState(null);
  // Whether to show "Full Scene" modal (only if level === 'scene_text')
  const [isFullSceneModalOpen, setIsFullSceneModalOpen] = useState(false);
const [expandedEvaluations, setExpandedEvaluations] = useState([]);
  
  // State and handlers for Suggestions Modal
  const [isSuggestionsModalOpen, setIsSuggestionsModalOpen] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [currentPlotItem, setCurrentPlotItem] = useState(null);
  const [loadingImplementationIdx, setLoadingImplementationIdx] = useState(null);
  // Track loading state per-plot-item for Get Suggestions
  const [loadingSuggestionsIds, setLoadingSuggestionsIds] = useState({});
  const [activeSuggestionFilter, setActiveSuggestionFilter] = useState('all'); // 'all', 'evaluation', 'regular'
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [expandedSuggestions, setExpandedSuggestions] = useState([]);
  const [isCopied, setIsCopied] = useState(false);

  // When suggestions are updated, refresh the modal's suggestions if it's open
  useEffect(() => {
    if (isSuggestionsModalOpen && currentPlotItem) {
      const combinedSuggestions = getCombinedSuggestions(currentPlotItem.id);
      setCurrentSuggestions(combinedSuggestions);
    }
  }, [suggestions, isSuggestionsModalOpen, currentPlotItem]);

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const expandAllSuggestions = () => setExpandedSuggestions(currentSuggestions.map((_, idx) => idx));
  const collapseAllSuggestions = () => setExpandedSuggestions([]);
  const toggleSuggestionExpansion = (idx) => {
    setExpandedSuggestions(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Combine all item contents into one text block for "View Full Scene"
  const fullSceneText = plotItems.map((item) => item.content).join('\n\n');
  const tooltipStyle = {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    zIndex: 1000,
    maxWidth: '300px',
    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
    pointerEvents: 'none',
    marginTop: '-35px',
    marginLeft: '50px',
    fontSize: '14px',
    lineHeight: '1.4'
  };
  // Debug logging
  useEffect(() => {
    console.log("PlotItems component mounted");
    console.log("onReplaceAllPlotItems:", typeof onReplaceAllPlotItems);
    console.log("plotItems:", plotItems);
  }, []);

  // Add this useEffect at the top of your PlotItems component
useEffect(() => {
  const adjustTextareaHeights = () => {
    // Get all textareas in the plot items table
    const textareas = document.querySelectorAll('textarea.form-control');
    
    textareas.forEach(textarea => {
      const cell = textarea.parentElement;
      const row = cell.parentElement;
      
      // Reset height to auto to get natural row height
      textarea.style.height = 'auto';
      
      // Get the row height after content settles
      setTimeout(() => {
        const rowHeight = row.offsetHeight;
        const cellPadding = parseInt(getComputedStyle(cell).paddingTop) + 
                          parseInt(getComputedStyle(cell).paddingBottom);
        
        // Set textarea height to fill the cell
        textarea.style.height = `${rowHeight - cellPadding - 4}px`;
      }, 0);
    });
  };

  // Run after component mounts and updates
  adjustTextareaHeights();
  
  // Re-run when window resizes
  window.addEventListener('resize', adjustTextareaHeights);
  
  return () => {
    window.removeEventListener('resize', adjustTextareaHeights);
  };
}, [plotItems]);

  // Sort plot items by number
  useEffect(() => {
    if (plotItems.length > 1) {
      // Sort items by number
      const sortedItems = [...plotItems].sort((a, b) => {
        if (a.number !== undefined && b.number !== undefined) {
          return a.number - b.number;
        }
        return 0;
      });
      
      // Only update if the order has changed
      if (JSON.stringify(sortedItems) !== JSON.stringify(plotItems)) {
        onReplaceAllPlotItems(sortedItems);
      }
    }
  }, [plotItems, onReplaceAllPlotItems]);

  // Close modal when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (isSuggestionsModalOpen) setIsSuggestionsModalOpen(false);
        if (isFullSceneModalOpen) setIsFullSceneModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isSuggestionsModalOpen, isFullSceneModalOpen]);

  // Combine evaluation suggestions with regular suggestions
  const getCombinedSuggestions = (plotItemId) => {
    // Get regular suggestions
    const regularSuggestions = suggestions?.plot_items?.[plotItemId] || [];
    
    // Get evaluation suggestions if available
    let evaluationSuggestions = [];
    const itemEvaluation = evaluations?.plot_items?.find(item => item.plot_item_id === plotItemId);
    
    if (itemEvaluation?.suggestions) {
      evaluationSuggestions = itemEvaluation.suggestions.map(suggestion => ({
        content: suggestion,
        action: 'append',
        source: 'evaluation'
      }));
    }
    
    // Return combined and sorted suggestions
    return [...regularSuggestions, ...evaluationSuggestions].sort((a, b) => {
      // Sort by linked todo items count (if any)
      const aLinkedCount = (a.linkedTodoIds?.length || 0);
      const bLinkedCount = (b.linkedTodoIds?.length || 0);
      return bLinkedCount - aLinkedCount;
    });
  };

  // Filter suggestions based on active filter
  const getFilteredSuggestions = (suggestions) => {
    if (activeSuggestionFilter === 'all') {
      return suggestions;
    }
    return suggestions.filter(suggestion => 
      activeSuggestionFilter === 'evaluation' 
        ? suggestion.source === 'evaluation'
        : suggestion.source !== 'evaluation'
    );
  };

  // Handler for mouse enter on score cell
const handleScoreMouseEnter = (itemId) => {
  setActiveTooltipId(itemId);
};

// Handler for mouse leave on score cell
const handleScoreMouseLeave = () => {
  setActiveTooltipId(null);
};

  const handleDismissSuggestion = (plotItemId, idx) => {
    // Get suggestions for this plot item
    const plotItemSuggestions = suggestions?.plot_items?.[plotItemId] || [];
    const updatedSuggestions = plotItemSuggestions.filter((_, i) => i !== idx);
    
    // Update the entire suggestions object
    setSuggestions((prev) => {
      const updated = { ...prev };
      
      if (!updated.plot_items) {
        updated.plot_items = {};
      }
      
      updated.plot_items[plotItemId] = updatedSuggestions;
      return updated;
    });
    
    // If the suggestions modal is open for this item, update local suggestions
    if (isSuggestionsModalOpen && currentPlotItem && currentPlotItem.id === plotItemId) {
      setCurrentSuggestions(updatedSuggestions);
    }
  };

  const getScoreColor = (score) => {
    // Round to nearest integer to ensure we're working with whole numbers 0-10
    const roundedScore = Math.round(score);
    
    // Create a gradient from red (0) to green (10)
    switch (roundedScore) {
      case 0: return '#ff0000'; // Red
      case 1: return '#ff2000';
      case 2: return '#ff4000';
      case 3: return '#ff6000';
      case 4: return '#ff8000'; // Orange
      case 5: return '#ffaa00'; // Amber
      case 6: return '#ffd000'; // Yellow
      case 7: return '#c0e000'; // Yellow-green
      case 8: return '#80c000'; 
      case 9: return '#60a000';
      case 10: return '#008800'; // Green
      default: return '#cccccc'; // Gray for any other value
    }
  };
  
  // Convert to array and sort
  const plotItemsArray = Array.isArray(plotItems) 
    ? plotItems 
    : Object.values(plotItems).sort((a, b) => {
      if (a.number !== undefined && b.number !== undefined) {
        return a.number - b.number;
      }
      return 0;
    });

// Provide a sensible label for the Add button based on current level
const addButtonLabel = (() => {
  switch (level) {
    case 'screenplay':
      return 'Add Scene';
    case 'sequence':
      return 'Add Sequence';
    case 'scene':
      return 'Add Beat';
    case 'scene_text':
      return 'Add Text';
    case 'scene_dialogue':
      return 'Add Line';
    default:
      return 'Add Item';
  }
})();

const handleGetImplementations = async (plotItem, suggestion, idx) => {
    try {
      setLoadingImplementationIdx(idx);
      
      const response = await axios.post(`/workspace/${workspaceUuid}/plot-item/${plotItem.id}/get-implementations`, {
        suggestion,
        workspaceData
      });
      
      if (onUpdateAiCalls && response.data.ai_calls !== undefined) {
        onUpdateAiCalls(response.data.ai_calls);
      }

      // The implementations are complete plot items
      const { implementations } = response.data;
      
      // Get the current suggestions
      const updatedSuggestions = [...(suggestions?.plot_items?.[plotItem.id] || [])];
      
      // Update the suggestion to include the implementations
      updatedSuggestions[idx] = {
        ...suggestion,
        implementations, // These are complete plot items
        isExpanded: true
      };
      
      // Update the suggestions in the main state
      setSuggestions((prev) => {
        const updated = { ...prev };
        
        if (!updated.plot_items) {
          updated.plot_items = {};
        }
        
        updated.plot_items[plotItem.id] = updatedSuggestions;
        return updated;
      });
      
      // Update the local modal state
      if (isSuggestionsModalOpen && currentPlotItem && currentPlotItem.id === plotItem.id) {
        setCurrentSuggestions(updatedSuggestions);
      }
    } catch (error) {
      console.error('Failed to get implementations:', error);
    } finally {
      setLoadingImplementationIdx(null);
    }
  };

  // Add a function to dismiss an implementation
  const handleDismissImplementation = (plotItemId, suggestionIdx, implementationIdx) => {
    // Get the current suggestions
    const plotItemSuggestions = suggestions?.plot_items?.[plotItemId] || [];
    
    if (plotItemSuggestions[suggestionIdx]?.implementations) {
      // Create a copy of the suggestions
      const updatedSuggestions = [...plotItemSuggestions];
      
      // Create a copy of the implementations with the specified one removed
      const updatedImplementations = updatedSuggestions[suggestionIdx].implementations.filter(
        (_, i) => i !== implementationIdx
      );
      
      // Update the suggestion with the new implementations array
      updatedSuggestions[suggestionIdx] = {
        ...updatedSuggestions[suggestionIdx],
        implementations: updatedImplementations
      };
      
      // Update the suggestions in the main state
      setSuggestions((prev) => {
        const updated = { ...prev };
        
        if (!updated.plot_items) {
          updated.plot_items = {};
        }
        
        updated.plot_items[plotItemId] = updatedSuggestions;
        return updated;
      });
      
      // Update the local modal state
      if (isSuggestionsModalOpen && currentPlotItem && currentPlotItem.id === plotItemId) {
        setCurrentSuggestions(updatedSuggestions);
      }
    }
  };

  // Handler to implement evaluation suggestion
  const handleImplementEvaluationSuggestion = (itemId, suggestion) => {
    const item = plotItems.find(item => item.id === itemId);
    
    if (item) {
      // Append the suggestion to item content
      const updatedContent = `${item.content}\n\n${suggestion}`;
      
      // Update the item
      onUpdate(itemId, updatedContent, null);
    }
  };

  // Handler to implement edit suggestion
  const handleImplementEditSuggestion = (plotItem, implementation, suggestionIdx) => {
    // Find the index of the current plot item
    const index = plotItems.findIndex((item) => item.id === plotItem.id);
    
    if (index !== -1) {
      // Create a copy of the current plot items array
      const updatedPlotItems = [...plotItems];
      
      // Remove the old plot item
      updatedPlotItems.splice(index, 1);
      
      // Add the new implementation (which already has proper number from backend)
      updatedPlotItems.push(implementation);
      
      // Sort the array by number if the number field exists
      updatedPlotItems.sort((a, b) => {
        if (a.number !== undefined && b.number !== undefined) {
          return a.number - b.number;
        }
        return 0; // Keep original order if numbers not available
      });
      
      // Call parent update function to replace the entire array
      if (typeof onReplaceAllPlotItems === 'function') {
        onReplaceAllPlotItems(updatedPlotItems);
      } else {
        // Fallback: If there's no direct way to replace all plot items,
        // simulate delete + add with existing handlers
        onDelete(index);
        onUpdate(null, implementation.content, implementation);
      }
      
      // Dismiss the suggestion after implementing
      handleDismissSuggestion(plotItem.id, suggestionIdx);
      
      // Close the suggestions modal
      setIsSuggestionsModalOpen(false);
    }
  };

  // Add a new PlotItem at the end (restore missing handler)
  const handleAddPlotItem = () => {
    const newPlotItem = {
      id: `${level}_${Date.now()}`,
      content: '',
      number: plotItemsArray.length + 1,
    };

    // Prefer bulk replace if parent provided that function
    if (typeof onReplaceAllPlotItems === 'function') {
      onReplaceAllPlotItems([...plotItemsArray, newPlotItem]);
    } else {
      // Fallback: call onUpdate with null index to indicate creation
      onUpdate(null, newPlotItem.content, newPlotItem);
    }
  };

  // Handler to open suggestions modal
  const handleOpenSuggestionsModal = (plotItem) => {
    // Get combined suggestions (regular + evaluation)
    const combinedSuggestions = getCombinedSuggestions(plotItem.id);
    
    setCurrentSuggestions(combinedSuggestions);
    setCurrentPlotItem(plotItem);
    setIsSuggestionsModalOpen(true);
    setActiveSuggestionFilter('all'); // Reset filter when opening modal
  };

  // Unified click handler for the suggestions button in the Actions column
  const handleSuggestionsButtonClick = (e, plotItem) => {
    e.stopPropagation();
    
    // If we're already loading suggestions for this item, do nothing
    if (loadingSuggestionsIds[plotItem.id]) return;

    // Open the modal immediately
    handleOpenSuggestionsModal(plotItem);

    // If there are no suggestions, fetch them.
    const combined = getCombinedSuggestions(plotItem.id);
    if (combined.length === 0) {
      handleGetPlotItemSuggestions(plotItem.id);
    }
  };

  // Handler to get suggestions for a plot item
  const handleGetPlotItemSuggestions = async (plotItemId) => {
     try {
      // mark this plot item as loading
      setLoadingSuggestionsIds(prev => ({ ...prev, [plotItemId]: true }));
       console.log("Requesting suggestions for plot item:", plotItemId);
       
       const response = await axios.post(`/workspace/${workspaceUuid}/plot-item/${plotItemId}/get-suggestions`, {
         workspaceData
       });
       
       console.log("Received response:", response.data);
      if (onUpdateAiCalls && response.data.ai_calls !== undefined) {
        onUpdateAiCalls(response.data.ai_calls);
      }

       if (response.data.success && response.data.suggestions) {
         setSuggestions(prev => {
           console.log("Previous suggestions:", prev);
           const updated = { ...prev };
           
           // Ensure you're properly merging the new suggestions
           Object.keys(response.data.suggestions).forEach(domain => {
             if (!updated[domain]) {
               updated[domain] = {};
             }
             
             Object.keys(response.data.suggestions[domain]).forEach(id => {
               if (!updated[domain][id]) {
                 updated[domain][id] = [];
               }
               
               updated[domain][id] = [
                 ...updated[domain][id],
                 ...response.data.suggestions[domain][id]
               ];
             });
           });
           
           console.log("Updated suggestions:", updated);
           return updated;
         });
       }
     } catch (error) {
       console.error("Error getting plot item suggestions:", error);
     } finally {
      // clear loading state for this plot item
      setLoadingSuggestionsIds(prev => {
        const copy = { ...prev };
        delete copy[plotItemId];
        return copy;
      });
     }
   };

  // Handle modal backdrop click
  const handleModalBackdropClick = (e, closeModalFn) => {
    if (e.target === e.currentTarget) {
      closeModalFn();
    }
  };

  // Get linked todo items for a suggestion
  const getLinkedTodoItems = (suggestion) => {
    if (!suggestion.linkedTodoIds || !todoItems) return [];
    return todoItems.filter(todo => suggestion.linkedTodoIds.includes(todo.id));
  };

  return (
    <div className="plot-items-container">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          {/* If we're at scene_text level, show a button to view the full compiled text */}
          {(level === 'scene_text' || level === 'scene_dialogue') && (
            <div className="btn-group" role="group">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setIsFullSceneModalOpen(true)}
              >
                View Full Scene
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleCopyToClipboard(fullSceneText)}
                style={{ width: '40px' }}
              >
                {isCopied ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faCopy} />}
              </button>
            </div>
          )}
        </div>
        <div>
          {/* Show Previous Evaluations button - only if there are evaluations */}
          {sceneEvaluations.length > 0 && (
            <button 
              className="btn btn-outline-info btn-sm me-2" 
              onClick={onShowPreviousEvaluations}
              title={`View ${sceneEvaluations.length} previous evaluation${sceneEvaluations.length !== 1 ? 's' : ''}`}
            >
              <FontAwesomeIcon icon={faTasks} className="me-1" />
              View Previous ({sceneEvaluations.length})
            </button>
          )}
          
          {/* Evaluate button with loading state */}
          <button 
            className="btn btn-primary btn-sm" 
            onClick={onEvaluateWorkspace}
            disabled={isEvaluatingWorkspace}
          >
            {isEvaluatingWorkspace ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Evaluating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTasks} className="me-1" />
                Evaluate
              </>
            )}
          </button>
        </div>
      </div>

{level === 'scene_dialogue' ? (
  <table className="table table-bordered">
    <thead>
      <tr>
        <th style={{ width: '5%' }}>Score</th>
        <th style={{ width: '15%' }}>Type</th>
        <th style={{ width: '70%' }}>Content</th>
        <th style={{ width: '10%' }}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {plotItemsArray.map((item, index) => {
        // Similar to your existing implementation but with dialogue handling
        const isSelected = item.id === selectedPlotItemId;
        const combinedSuggestions = getCombinedSuggestions(item.id);
        const suggestionCount = combinedSuggestions.length;
        const isDraggable = level !== 'scene_text';
        
        return (
          <React.Fragment key={item.id}>
            <tr
              className={isSelected ? 'table-primary' : ''}
              onClick={() => {
                setSelectedPlotItemId(item.id);
                onSelectPlotItem(item);
              }}
              draggable={isDraggable}
              onDragStart={() => {
                if (isDraggable) setDraggedIndex(index);
              }}
              onDragOver={(e) => {
                if (isDraggable) e.preventDefault();
              }}
              onDrop={() => {
                if (isDraggable && draggedIndex !== null && draggedIndex !== index) {
                  onReorder(draggedIndex, index);
                  setDraggedIndex(null);
                }
              }}
            >
              {/* Score Column - different for dialogue */}
              <td 
                className="text-center align-middle p-0"
                style={{ 
                  backgroundColor: item.type === 'dialogue' ? getScoreColor(item.score || 5) : '#f8f9fa',
                  position: 'relative',
                  cursor: item.explanation ? 'help' : 'default'
                }}
                onMouseEnter={() => item.explanation && handleScoreMouseEnter(item.id)}
                onMouseLeave={handleScoreMouseLeave}
              >
                {item.type === 'dialogue' && item.score !== undefined ? (
                  <div className="d-flex flex-column justify-content-center align-items-center h-100"
                    style={{ 
                      backgroundColor: getScoreColor(item.score),
                      color: item.score > 5 ? '#000' : '#fff',
                      height: '100%',
                      padding: '0.5rem 0'
                    }}
                  >
                    <strong>{item.score}</strong>
                    {item.explanation && <FontAwesomeIcon icon={faInfoCircle} style={{ opacity: 0.7, marginTop: '4px' }}/>}
                  </div>
                ) : (
                  <div className="text-center">-</div>
                )}
                
                {/* Tooltip for explanation */}
                {activeTooltipId === item.id && item.explanation && (
                  <div style={tooltipStyle}>
                    {item.explanation}
                  </div>
                )}
              </td>
              
              {/* Type Column - Simplified */}
              <td className="align-middle">
                <strong>{item.type === 'dialogue' ? 'Dialogue' : 'Scene Text'}</strong>
                {item.number && <span> ({item.number})</span>}
              </td>

              {/* Content Column */}
              <td>
                <textarea
                  className="form-control"
                  value={item.content}
                  onChange={(e) => onUpdate(item.id, e.target.value, null)}
                  rows={4}
                  style={{
                    backgroundColor: item.type === 'dialogue' ? '#f8f8ff' : '#fff' // Light blue bg for dialogue
                  }}
                  placeholder={`Enter ${ 
                    item.type === 'dialogue' ? 'dialogue' : 'scene text'
                  }...`}
                />
                {/* Suggestions button: single compact control showing lightbulb icon + count */}
                {suggestionCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm d-flex align-items-center"
                    onClick={(e) => { e.stopPropagation(); handleOpenSuggestionsModal(item); }}
                    aria-label={`${suggestionCount} suggestions`}
                    title={`${suggestionCount} suggestions`}
                    disabled={!!loadingSuggestionsIds[item.id]}
                    style={{ gap: '6px', padding: '0.25rem 0.5rem' }}
                  >
                    <FontAwesomeIcon icon={faLightbulb} />
                    <span className="fw-bold">{suggestionCount}</span>
                  </button>
                )}
              </td>

              {/* Actions Column */}
              <td className="text-center">
                {/* Drag Handle button */}
                <button
                  className="btn btn-outline-secondary btn-sm me-2"
                  disabled
                  aria-label="Drag Handle"
                >
                  <FontAwesomeIcon icon={faGripVertical} />
                </button>

                {/* Suggestions button (icon + count) placed in Actions column */}
                {suggestionCount > 0 ? (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm me-2 d-flex align-items-center"
                    onClick={(e) => handleSuggestionsButtonClick(e, item)}
                    disabled={!!loadingSuggestionsIds[item.id]}
                    title={`${suggestionCount} suggestions`}
                    style={{ gap: '6px', padding: '0.25rem 0.6rem' }}
                  >
                    {loadingSuggestionsIds[item.id] ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faLightbulb} />
                    )}
                    <span className="ms-2 fw-bold">{suggestionCount}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm me-2"
                    onClick={(e) => handleSuggestionsButtonClick(e, item)}
                    aria-label="Get Suggestions"
                    disabled={!!loadingSuggestionsIds[item.id]}
                  >
                    {loadingSuggestionsIds[item.id] ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faLightbulb} />
                    )}
                  </button>
                )}

                {/* Delete button */}
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item.id);
                  }}
                  aria-label="Delete Plot Item"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </td>
            </tr>
          </React.Fragment>
        );
      })}
    </tbody>
  </table>
) : (     
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>Score</th>
            <th style={{ width: '25%' }}>Beat Summary</th>
            <th style={{ width: '60%' }}>Beat Text</th>
            <th style={{ width: '10%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
        {plotItemsArray.map((item, index) => {
            // Find the matching evaluation
            const evaluation = evaluations?.plot_items?.find(evalItem => 
              evalItem.plot_item_id === item.id
            );
            
            const isEvaluationExpanded = expandedEvaluations.includes(item.id);
            const isSelected = item.id === selectedPlotItemId;
            
            // Get combined suggestions (regular + evaluation)
            const combinedSuggestions = getCombinedSuggestions(item.id);
            const suggestionCount = combinedSuggestions.length;
            
            // Decide if item is draggable
            const isDraggable = level !== 'scene_text';

            return (
              <React.Fragment key={item.id}>
              <tr
                className={isSelected ? 'table-primary' : ''}
                onClick={() => {
                  setSelectedPlotItemId(item.id);
                  onSelectPlotItem(item);
                }}
                draggable={isDraggable}
                onDragStart={() => {
                  if (isDraggable) setDraggedIndex(index);
                }}
                onDragOver={(e) => {
                  if (isDraggable) e.preventDefault();
                }}
                onDrop={() => {
                  if (isDraggable && draggedIndex !== null && draggedIndex !== index) {
                    onReorder(draggedIndex, index);
                    setDraggedIndex(null);
                  }
                }}
              >
<td 
  className="text-center align-middle p-0"
  style={{ 
    backgroundColor: getScoreColor(item.score),
    position: 'relative',
    cursor: item.explanation ? 'help' : 'default'
  }}
  onMouseEnter={() => item.explanation && handleScoreMouseEnter(item.id)}
  onMouseLeave={handleScoreMouseLeave}
>
  {item.score !== undefined ? (
    <div className="d-flex flex-column justify-content-center align-items-center h-100"
      style={{ 
        backgroundColor: getScoreColor(item.score),
        color: item.score > 5 ? '#000' : '#fff',
        height: '100%',
        padding: '0.5rem 0'
      }}
    >
      <strong>{item.score}</strong>
      {item.explanation && <FontAwesomeIcon icon={faInfoCircle} style={{ opacity: 0.7, marginTop: '4px' }}/>}
    </div>
  ) : (
    <div className="text-center">-</div>
  )}
  
  {/* Tooltip for explanation */}
  {activeTooltipId === item.id && item.explanation && (
    <div style={tooltipStyle}>
      {item.explanation}
    </div>
  )}
</td>
                {/* Beat Summary Column */}
                <td>
                  {/* Title replaces "Text Item #X" if present, else fallback to label + number */}
                  <strong>{item.title || `Beat ${item.number}`}</strong>
                  {item.number && <span> ({item.number})</span>}
                  {/* If summary is present, show it below title */}
                  <p className="text-muted mb-0">{item.summary || 'No summary available'}</p>
                </td>

                {/* Beat Text Column */}
                <td>
                  <textarea
                    className="form-control"
                    value={item.content}
                    onChange={(e) => onUpdate(item.id, e.target.value, null)}
                    // rows={3}
                    placeholder={`Enter ${ 
                      level === 'screenplay'
                        ? 'scene summary'
                        : level === 'sequence'
                        ? 'sequence summary'
                        : level === 'scene'
                        ? 'beat'
                        : 'text'
                    }...`}
                  />
                  {/* Suggestions button: single compact control showing lightbulb icon + count */}
                  {suggestionCount > 0 && false && (
                    <button
                      type="button"
                      className="btn btn-warning btn-sm d-flex align-items-center"
                      onClick={(e) => { e.stopPropagation(); handleOpenSuggestionsModal(item); }}
                      aria-label={`${suggestionCount} suggestions`}
                      title={`${suggestionCount} suggestions`}
                      disabled={!!loadingSuggestionsIds[item.id]}
                      style={{ gap: '6px', padding: '0.25rem 0.5rem' }}
                    >
                      <FontAwesomeIcon icon={faLightbulb} />
                      <span className="fw-bold">{suggestionCount}</span>
                    </button>
                  )}
                </td>

                {/* Actions Column */}
                <td className="text-center">
                {/* Suggestions button (icon + count) placed in Actions column */}
                {suggestionCount > 0 ? (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm me-2 d-flex align-items-center"
                    onClick={(e) => handleSuggestionsButtonClick(e, item)}
                    disabled={!!loadingSuggestionsIds[item.id]}
                    title={`${suggestionCount} suggestions`}
                    style={{ gap: '6px', padding: '0.25rem 0.6rem' }}
                  >
                    {loadingSuggestionsIds[item.id] ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faLightbulb} />
                    )}
                    <span className="ms-2 fw-bold">{suggestionCount}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm me-2"
                    onClick={(e) => handleSuggestionsButtonClick(e, item)}
                    aria-label="Get Suggestions"
                    disabled={!!loadingSuggestionsIds[item.id]}
                  >
                    {loadingSuggestionsIds[item.id] ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faLightbulb} />
                    )}
                  </button>
                )}

                {/* Delete button */}
                {/* <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item.id);
                  }}
                  aria-label="Delete Plot Item"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button> */}
               </td>
              </tr>
              </React.Fragment>
              );
          })}
        </tbody>
      </table>
)}

      {/* Add New PlotItem Button */}
      <button className="btn btn-outline-primary btn-sm" onClick={handleAddPlotItem}>
        <FontAwesomeIcon icon={faPlus} /> {addButtonLabel}
      </button>

      {/* Full Scene Modal (only for scene_text) */}
      {(level === 'scene_text' || level === 'scene_dialogue') && isFullSceneModalOpen && (
<div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={(e) => handleModalBackdropClick(e, () => setIsFullSceneModalOpen(false))}
        >
          <div className="modal-dialog modal-lg" style={{ marginTop: '10vh' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Full Scene Text</h5>
                <div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm me-2"
                    onClick={() => handleCopyToClipboard(fullSceneText)}
                  >
                    {isCopied ? (
                      <span><FontAwesomeIcon icon={faCheck} /> Copied!</span>
                    ) : (
                      <span><FontAwesomeIcon icon={faCopy} /> Copy</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsFullSceneModalOpen(false)}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
              <div className="modal-body">
                <pre style={{ whiteSpace: 'pre-wrap' }}>{fullSceneText}</pre>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsFullSceneModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Modal */}
      {isSuggestionsModalOpen && currentPlotItem && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={(e) => handleModalBackdropClick(e, () => setIsSuggestionsModalOpen(false))}
        >
          <div className="modal-dialog modal-xl" style={{ marginTop: '10vh', maxHeight: '80vh' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Suggestions for {
                    level === 'screenplay'
                      ? 'Scene'
                      : level === 'sequence'
                      ? 'Sequence'
                      : level === 'scene'
                      ? 'Beat'
                      : 'Text'
                  } #{currentPlotItem.number}
                </h5>
                <div className="ms-auto me-2">
                  <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className="btn btn-outline-secondary" onClick={expandAllSuggestions}>Expand All</button>
                    <button type="button" className="btn btn-outline-secondary" onClick={collapseAllSuggestions}>Collapse All</button>
                  </div>
                </div>
                {/* <div className="ms-auto me-3">
                  <div className="btn-group" role="group">
                    <button 
                      type="button" 
                      className={`btn btn-sm ${activeSuggestionFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setActiveSuggestionFilter('all')}
                    >
                      All
                    </button>
                    <button 
                      type="button" 
                      className={`btn btn-sm ${activeSuggestionFilter === 'regular' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setActiveSuggestionFilter('regular')}
                    >
                      Regular
                    </button>
                    <button 
                      type="button" 
                      className={`btn btn-sm ${activeSuggestionFilter === 'evaluation' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setActiveSuggestionFilter('evaluation')}
                    >
                      Evaluation
                    </button>
                  </div>
                </div> */}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsSuggestionsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loadingSuggestionsIds[currentPlotItem.id] ? (
                  <div className="text-center p-5">
                    <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                    <p className="mt-3">Getting suggestions...</p>
                  </div>
                ) : currentSuggestions.length > 0 ? (
                  <div className="list-group">
                    {getFilteredSuggestions(currentSuggestions).map((suggestion, idx) => {
                      const linkedTodos = getLinkedTodoItems(suggestion);
                      const isExpanded = expandedSuggestions.includes(idx);
                      const hasImplementations = suggestion.implementations && suggestion.implementations.length > 0;

                      return (
                        <div key={idx} className="list-group-item mb-2 border p-3">
                          <div 
                            className="d-flex justify-content-between align-items-center"
                            onClick={() => toggleSuggestionExpansion(idx)} 
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-center">
                              <span className={`badge ${ 
                                suggestion.action === 'replace' ? 'bg-danger' : 
                                suggestion.action === 'edit' ? 'bg-primary' : 
                                'bg-success'
                              } me-2`}>
                                {suggestion.action}
                              </span>
                              {suggestion.issue && (
                                <span className="fw-bold text-dark">
                                  {suggestion.issue}
                                </span>
                              )}
                            </div>
                            <button className="btn btn-sm btn-light">
                              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="mt-3">
                              <div>
                                {/* Original Suggestion Details */}
                                <div className="p-3 bg-light rounded mb-3 border">
                                  <p className="mb-0" style={{ fontFamily: "monospace" }}>{suggestion.content}</p>
                                </div>

                                {suggestion.impact && (
                                  <div className="mb-2">
                                    <strong className="text-success">Impact:</strong>
                                    <p className="mb-0 small">{suggestion.impact}</p>
                                  </div>
                                )}

                                {suggestion.theory && (
                                  <div className="p-2 mt-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                    <small className="text-muted">
                                      <strong>Theory:</strong> <em>{suggestion.theory}</em>
                                    </small>
                                  </div>
                                )}
                              </div>

                              {hasImplementations && (
                                <div className="p-3 border-start border-primary border-3" style={{ backgroundColor: 'rgba(0, 123, 255, 0.05)' }}>
                                  <h6 className="text-primary">Implementation Options:</h6>
                                  {suggestion.implementations.map((implementation, implIdx) => (
                                    <div key={implIdx} className="border-top mt-2 pt-2">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="badge bg-info">Option {implIdx + 1}</span>
                                        <div>
                                          <button
                                            className="btn btn-outline-success btn-sm me-2"
                                            onClick={() => handleImplementEditSuggestion(currentPlotItem, implementation, idx)}
                                          >
                                            Use This
                                          </button>
                                          <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => handleDismissImplementation(currentPlotItem.id, idx, implIdx)}
                                          >
                                            Dismiss
                                          </button>
                                        </div>
                                      </div>
                                      {implementation.summary && (
                                        <div className="small text-muted mb-1">
                                          <strong>Summary:</strong> {implementation.summary}
                                        </div>
                                      )}
                                      <p className="mb-0 mt-1 font-monospace">{implementation.content}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Spinner for loading implementations, shown only for the specific item */}
                              {loadingImplementationIdx === idx && (
                                <div className="text-center p-4">
                                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                  <p className="mt-2">Generating options...</p>
                                </div>
                              )}

                              {/* Linked Todos */}
                              {linkedTodos.length > 0 && (
                                <div className="mt-3 mb-2 ps-2 border-start border-2 border-secondary">
                                  <small className="text-muted">Linked Todo Items:</small>
                                  <ul className="list-unstyled mb-0 ps-2">
                                    {linkedTodos.map(todo => (
                                      <li key={todo.id} className="small">
                                        <FontAwesomeIcon 
                                          icon={todo.completed ? faCheck : faTasks} 
                                          className={`me-1 ${todo.completed ? 'text-success' : 'text-secondary'}`}
                                        />
                                        <span className={todo.completed ? 'text-decoration-line-through' : ''}>
                                          {todo.content.length > 60 ? `${todo.content.substring(0, 60)}...` : todo.content}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="text-end mt-3">
                                {suggestion.action === 'edit' && !hasImplementations && (
                                  <button
                                    className="btn btn-outline-primary btn-sm me-2"
                                    onClick={() => handleGetImplementations(currentPlotItem, suggestion, idx)}
                                    disabled={loadingImplementationIdx !== null}
                                  >
                                    Get Options
                                  </button>
                                )}
                                
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => handleDismissSuggestion(currentPlotItem.id, idx)}
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center">No suggestions available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsSuggestionsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PlotItems.propTypes = {
  plotItems: PropTypes.array.isRequired,
  level: PropTypes.oneOf(['screenplay', 'sequence', 'scene', 'scene_text','scene_dialogue']).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  suggestions: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]).isRequired,
  setSuggestions: PropTypes.func.isRequired,
  onReplaceAllPlotItems: PropTypes.func, 
  workspaceUuid: PropTypes.string.isRequired,
  workspaceData: PropTypes.object.isRequired,
  onSelectPlotItem: PropTypes.func.isRequired,
  evaluations: PropTypes.object,
  onSendQuestionToChat: PropTypes.func,
  todoItems: PropTypes.array,
  onAddTodoItem: PropTypes.func,
  onGetSuggestions: PropTypes.func.isRequired,
  onEvaluateWorkspace: PropTypes.func.isRequired,
  onShowPreviousEvaluations: PropTypes.func, // New prop
  sceneEvaluations: PropTypes.array, // New prop
  isEvaluatingWorkspace: PropTypes.bool // New prop
};

export default PlotItems;
